import tocbot from 'tocbot';

// Initialize TOC (Table of Contents)
export function initTOC() {
  // Only initialize on post/page templates
  const isPostPage = document.body.classList.contains('post-template') || 
                    document.body.classList.contains('page-template') ||
                    document.querySelector('.js-toc-content');
  
  if (!isPostPage) return;

  const tocElement = document.querySelector('.js-toc');
  const contentElement = document.querySelector('.js-toc-content');
  
  if (!tocElement || !contentElement) return;

  const tocHeaderTitle = document.querySelector('.toc-header__title');
  const defaultTitle = tocHeaderTitle?.textContent?.trim() || 'Overview';
  const headings = Array.from(contentElement.querySelectorAll('h2'));

  tocbot.init({
    // Where to render the table of contents.
    tocSelector: '.js-toc',
    // Where to grab the headings to build the table of contents.
    contentSelector: '.js-toc-content',
    // Which headings to grab inside of the contentSelector element.
    headingSelector: 'h2',
    // Offset for smooth scrolling (scrolls a bit higher so heading is visible)
    scrollSmoothOffset: -100,
  });

  function getHeadingTitleForScroll() {
    if (!headings.length) {
      return defaultTitle;
    }

    const thresholdFromTop = 120;
    let currentHeading = null;

    for (const heading of headings) {
      const rect = heading.getBoundingClientRect();
      const headingTop = rect.top;
      const headingBottom = rect.bottom;

      // If heading is fully above the threshold, keep it as candidate and continue
      if (headingBottom < thresholdFromTop) {
        currentHeading = heading;
        continue;
      }

      // First heading not yet reached the threshold
      if (!currentHeading && headingTop > thresholdFromTop) {
        return defaultTitle;
      }

      // Heading currently crossing the threshold area
      if (headingTop <= thresholdFromTop && headingBottom >= thresholdFromTop) {
        currentHeading = heading;
        break;
      }
    }

    if (currentHeading && currentHeading.textContent) {
      return currentHeading.textContent.trim();
    }

    // Fallback: if scrolled past all headings, keep the last one
    const lastHeading = headings[headings.length - 1];
    if (lastHeading && lastHeading.textContent) {
      return lastHeading.textContent.trim();
    }

    return defaultTitle;
  }

  function updateTocHeaderTitle(nextTitle) {
    if (!tocHeaderTitle) return;

    const titleToSet = (nextTitle && nextTitle.trim()) || getHeadingTitleForScroll();
    if (!titleToSet || tocHeaderTitle.textContent === titleToSet) return;

    tocHeaderTitle.textContent = titleToSet;
  }

  // Set initial title after TOC is built and active link is determined
  setTimeout(() => {
    updateTocHeaderTitle();
  }, 150);

  // Close TOC when clicking on a link
  tocElement.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (link) {
      // Immediately update header title to clicked heading
      updateTocHeaderTitle(link.textContent || '');

      // Close TOC after a short delay to allow smooth scroll to start
      setTimeout(() => {
        closeTOC();
      }, 100);
    }
  });

  // Update title on scroll based on active heading
  document.addEventListener('scroll', () => {
    updateTocHeaderTitle();
  }, { passive: true });

  // Position fixed TOC using in-flow anchor element
  const tocContainer = document.querySelector('.toc-container');
  const tocAnchor = document.querySelector('.toc-anchor');

  function syncTocPosition() {
    if (!tocAnchor || !tocContainer) return;
    const rect = tocAnchor.getBoundingClientRect();
    tocContainer.style.left = `${rect.left}px`;
    tocContainer.style.width = `${rect.width}px`;
  }

  syncTocPosition();
  window.addEventListener('resize', syncTocPosition);

  // Track anchor position frame-by-frame during sidebar transition
  document.querySelector('.toggle-sidebar-button')?.addEventListener('click', () => {
    const end = Date.now() + 350;
    function trackPosition() {
      syncTocPosition();
      if (Date.now() < end) requestAnimationFrame(trackPosition);
    }
    requestAnimationFrame(trackPosition);
  });

  // Show TOC after first h2 is scrolled past, hide near post-promo
  const firstH2 = headings[0];
  const postPromo = document.querySelector('.post-promo');
  let scrolledPastH2 = false;

  function updateTocVisibility() {
    if (!tocContainer) return;
    let nearPostPromo = false;
    if (postPromo) {
      nearPostPromo = postPromo.getBoundingClientRect().top <= 100;
    }
    tocContainer.classList.toggle('toc-container--visible', scrolledPastH2 && !nearPostPromo);
  }

  if (firstH2) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        scrolledPastH2 = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        updateTocVisibility();
      });
    }, { threshold: 0 });
    observer.observe(firstH2);
  }

  document.addEventListener('scroll', updateTocVisibility, { passive: true });
}

// Helper function to close TOC
function closeTOC() {
  const tocHeader = document.querySelector('.toc-header');
  const tocContent = document.querySelector('.js-toc');
  const arrowIcon = document.querySelector('.toc-header__arrow-icon');
  
  if (tocHeader && tocContent && arrowIcon) {
    tocHeader.classList.remove('is-open');
    arrowIcon.classList.remove('is-open');
    tocContent.classList.remove('is-open');
  }
}

// TOC Accordion functionality
export function initTOCAccordion() {
  // Only initialize on post/page templates
  const isPostPage = document.body.classList.contains('post-template') || 
                    document.body.classList.contains('page-template') ||
                    document.querySelector('.js-toc-content');
  
  if (!isPostPage) return;

  const tocHeader = document.querySelector('.toc-header');
  const tocContent = document.querySelector('.js-toc');
  const arrowIcon = document.querySelector('.toc-header__arrow-icon');
  let isHidden = true;
  if (!tocHeader || !tocContent || !arrowIcon) return;


  // Toggle function
  function toggleTOC() {
    isHidden = !tocHeader.classList.contains('is-open');

    if (isHidden) {
      // Open
      tocHeader.classList.add('is-open');
      arrowIcon.classList.add('is-open');
      tocContent.classList.add('is-open');
    } else {
      // Close
      tocHeader.classList.remove('is-open');
      arrowIcon.classList.remove('is-open');
      tocContent.classList.remove('is-open');
    }
  }

  // Add click event listener
  tocHeader.addEventListener('click', toggleTOC);
}

